import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError , toastError} from "../../lib/api";
import { PageHeader, Pill, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { KanbanSquare, Plus, ArrowRight, Layers, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useCanWrite } from "../../lib/roles";

const CANONICAL_ORDER = ["prospect_identified", "initial_contact", "qualified_opportunity", "discovery_complete", "solution_presented", "decision_pending", "closed_won", "closed_lost"];

export default function OpportunitiesKanban() {
  const canWrite = useCanWrite();
  const [opps, setOpps] = useState([]);
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ name: "", value: 0, pipeline_id: "", stage_id: "", contact_id: null, organization_id: null, expected_close_date: "" });
  const [contacts, setContacts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [movingOpp, setMovingOpp] = useState(null);
  const [targetStage, setTargetStage] = useState("");
  const [conflict, setConflict] = useState(null); // 409 dialog state

  const load = async () => {
    try {
      const [o, c, cts, orgs, requirements] = await Promise.all([
        api.get("/crm/opportunities"),
        api.get("/config/viewer"),
        api.get("/crm/contacts", { params: { limit: 200 } }),
        api.get("/crm/organizations", { params: { limit: 200, archived: false } }),
        api.get("/tenant/workflow-requirements"),
      ]);
      setOpps(o.data);
      setConfig(c.data);
      setContacts(cts.data);
      setOrganizations(orgs.data || []);
      setRequiredFields(requirements.data?.required_fields?.opportunity || []);
      if (!form.pipeline_id && c.data.pipelines[0]) {
        const pipelineId = c.data.pipelines[0].id;
        const firstStage = (c.data.stages || [])
          .filter((stage: any) => stage.pipeline_id === pipelineId)
          .sort((left: any, right: any) => (left.order || 0) - (right.order || 0))[0];
        setForm((f) => ({ ...f, pipeline_id: pipelineId, stage_id: firstStage?.id || "" }));
      }
    } catch (e) { toastError(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stagesByCanonical = useMemo(() => {
    const map = {};
    (config?.stages || []).forEach((s) => { if (!map[s.canonical_stage]) map[s.canonical_stage] = s; });
    return map;
  }, [config]);

  const oppsByCanonical = useMemo(() => {
    const g = {};
    CANONICAL_ORDER.forEach((c) => (g[c] = []));
    opps.filter((o) => !o.archived).forEach((o) => {
      (g[o.canonical_stage] || (g[o.canonical_stage] = [])).push(o);
    });
    return g;
  }, [opps]);

  const create = async () => {
    try {
      await api.post("/crm/opportunities", {
        name: form.name, value: Number(form.value),
        pipeline_id: form.pipeline_id, stage_id: form.stage_id,
        contact_id: form.contact_id || null,
        organization_id: form.organization_id || null,
        expected_close_date: form.expected_close_date || null,
      });
      toast.success("Opportunity created");
      const pipelineId = config.pipelines[0]?.id || "";
      const firstStage = (config.stages || [])
        .filter((stage: any) => stage.pipeline_id === pipelineId)
        .sort((left: any, right: any) => (left.order || 0) - (right.order || 0))[0];
      setOpen(false); setForm({ name: "", value: 0, pipeline_id: pipelineId, stage_id: firstStage?.id || "", contact_id: null, organization_id: null, expected_close_date: "" });
      load();
    } catch (e) { toastError(e); }
  };

  const moveStage = async (opp, toStageId) => {
    try {
      await api.post(`/crm/opportunities/${opp.id}/stage`, {
        version: opp.version, to_stage_id: toStageId,
      }, { headers: { "Idempotency-Key": crypto.randomUUID() } });
      toast.success("Stage updated");
      setMovingOpp(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "version_conflict") {
        setConflict({ opp, server: d.current, targetStageId: toStageId });
        return;
      }
      toast.error(formatApiError(d));
    }
  };

  const resolveConflict = async () => {
    if (!conflict) return;
    await moveStage(conflict.server, conflict.targetStageId);
    setConflict(null);
  };

  return (
    <>
      <PageHeader title="Opportunities" subtitle="Kanban board · 8 canonical stages · atomic stage moves"
        actions={canWrite ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" disabled={!config} data-testid="create-opp-btn"><Plus className="w-4 h-4 mr-1" /> New opportunity</Button>
            </DialogTrigger>
            <DialogContent data-testid="create-opp-dialog">
              <DialogHeader><DialogTitle>New opportunity</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name{requiredFields.includes("name") ? " *" : ""}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="opp-name" /></div>
                <div><Label>Value{requiredFields.includes("value") ? " *" : ""}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="opp-value" /></div>
                <div><Label>Pipeline</Label>
                  <Select value={form.pipeline_id} onValueChange={(v) => setForm({ ...form, pipeline_id: v, stage_id: "" })}>
                    <SelectTrigger data-testid="opp-pipeline"><SelectValue /></SelectTrigger>
                    <SelectContent>{(config?.pipelines || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Stage</Label>
                  <Select value={form.stage_id} onValueChange={(v) => setForm({ ...form, stage_id: v })}>
                    <SelectTrigger data-testid="opp-stage"><SelectValue placeholder="Choose stage" /></SelectTrigger>
                    <SelectContent>{(config?.stages || []).filter((s) => s.pipeline_id === form.pipeline_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Contact{requiredFields.includes("contact_id") ? " *" : " (optional)"}</Label>
                  <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="opp-contact"><SelectValue placeholder="No contact" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Organization / clinic{requiredFields.includes("organization_id") ? " *" : " (optional)"}</Label>
                  <Select value={form.organization_id || "none"} onValueChange={(v) => setForm({ ...form, organization_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="opp-organization"><SelectValue placeholder="No organization" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {organizations.map((org: any) => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Expected close date{requiredFields.includes("expected_close_date") ? " *" : " (optional)"}</Label>
                  <Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} data-testid="opp-expected-close-date" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={!form.name || !form.stage_id || requiredFields.some((field) => field !== "owner_id" && !String(form[field] ?? "").trim())} data-testid="opp-submit">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      />
      <div className="max-w-[100vw] p-6 md:p-8 overflow-x-auto">
        <div className="flex gap-3" data-testid="kanban-board" style={{ minWidth: `${CANONICAL_ORDER.length * 320}px` }}>
          {CANONICAL_ORDER.map((canon) => {
            const stage = stagesByCanonical[canon];
            return (
              <div key={canon} className="w-[300px] shrink-0" data-testid={`kanban-column-${canon}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{canon.replace(/_/g, " ")}</div>
                  <span className="text-xs text-slate-400">{oppsByCanonical[canon]?.length || 0}</span>
                </div>
                <div className="space-y-2 min-h-[120px]">
                  {(oppsByCanonical[canon] || []).map((o) => (
                    <div key={o.id} className="bg-white border border-slate-200 rounded-md p-3 hover:ring-1 hover:ring-slate-300 transition-shadow" data-testid={`opp-card-${o.id.slice(0,8)}`}>
                      <div className="text-sm font-medium truncate">{o.name}</div>
                      <div className="text-xs text-slate-500 mt-1">${o.value.toLocaleString()} · v{o.version}</div>
                      <div className="mt-2 flex items-center gap-3">
                        <Link
                          to={`/opportunities/${o.id}`}
                          className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
                          data-testid={`opp-open-${o.id.slice(0,8)}`}
                          aria-label={`Open opportunity ${o.name}`}
                        >
                          Open details <ExternalLink className="w-3 h-3" />
                        </Link>
                        {canWrite && (
                          <button className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" onClick={() => { setMovingOpp(o); setTargetStage(""); }} data-testid={`move-${o.id.slice(0,8)}`}>
                            <ArrowRight className="w-3 h-3" /> Move stage
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(oppsByCanonical[canon] || []).length === 0 && (
                    <div className="text-xs text-slate-400 italic px-2 py-3">No opportunities</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Move-stage dialog (click-to-move fallback) */}
      <Dialog open={!!movingOpp} onOpenChange={(v) => !v && setMovingOpp(null)}>
        <DialogContent data-testid="move-stage-dialog">
          <DialogHeader><DialogTitle>Move stage</DialogTitle></DialogHeader>
          {movingOpp && config && (
            <div className="space-y-3">
              <div className="text-sm">Opportunity: <span className="font-medium">{movingOpp.name}</span></div>
              <div className="text-xs text-slate-500">Current canonical stage: <span className="font-mono">{movingOpp.canonical_stage}</span></div>
              <div><Label>Target stage</Label>
                <Select value={targetStage} onValueChange={setTargetStage}>
                  <SelectTrigger data-testid="move-target"><SelectValue placeholder="Choose target stage" /></SelectTrigger>
                  <SelectContent>{config.stages.filter((s) => s.pipeline_id === movingOpp.pipeline_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button onClick={() => moveStage(movingOpp, targetStage)} disabled={!targetStage} data-testid="move-confirm">Move</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 409 conflict dialog */}
      <Dialog open={!!conflict} onOpenChange={(v) => !v && setConflict(null)}>
        <DialogContent className="max-w-2xl" data-testid="conflict-dialog">
          <DialogHeader><DialogTitle className="text-amber-700">Stale record — merge required</DialogTitle></DialogHeader>
          {conflict && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">This opportunity was updated by someone else while you were working on it. Review the server version and confirm your intended move.</p>
              <div className="grid grid-cols-2 gap-3 text-sm font-mono">
                <div className="border border-slate-200 rounded p-3 bg-slate-50">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Your version</div>
                  <div>Stage: {conflict.opp.canonical_stage}</div>
                  <div>Version: {conflict.opp.version}</div>
                </div>
                <div className="border border-amber-200 rounded p-3 bg-amber-50">
                  <div className="text-xs uppercase tracking-widest text-amber-700 mb-1">Server version</div>
                  <div>Stage: {conflict.server.canonical_stage}</div>
                  <div>Version: {conflict.server.version}</div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConflict(null)} data-testid="conflict-cancel">Cancel</Button>
                <Button onClick={resolveConflict} data-testid="conflict-retry">Apply move on latest version</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
